/**
 *  GameScene
 *
 *  Created by Legman on 21/02/2017.
 *
 */

import AI from "../AI";
import AudioParams from "../AudioParams";
import Common from "../Common";
import Hud from "../Hud";
import Player from "../Player";
import Pickup from "../Pickup";
import PickupSelector from "../PickupSelector";
import Powerup from "../powerups/Powerup";
import Skybox from "../Skybox";

import DoomBuggyView from "../cars/DoomBuggyView";
import PussycatView from "../cars/PussycatView";
import MeanMachineView from "../cars/MeanMachineView";
import IQView from "../cars/IQView";
import TurboView from "../cars/TurboView";

import BombPowerup from "../powerups/BombPowerup";
import BoostPowerup from "../powerups/BoostPowerup";
import BubblesPowerup from "../powerups/BubblesPowerup";
import ButterfliesPowerup from "../powerups/ButterfliesPowerup";
import CandyPowerup from "../powerups/CandyPowerup";
import FlamePowerup from "../powerups/FlamePowerup";
import GluePowerup from "../powerups/GluePowerup";
import LightingPowerup from "../powerups/LightningPowerup";
import MissilePowerup from "../powerups/MissilePowerup";
import SmartMissilePowerup from "../powerups/SmartMissilePowerup";
import SteamPowerup from "../powerups/SteamPowerup";
import TacsPowerup from "../powerups/TacsPowerup";

import RaceCar from "../race/RaceCar";
import RaceEngine from "../race/RaceEngine";
import RaceEntity from "../race/RaceEntity";
import RaceTrack from "../race/RaceTrack";
import RaceView from "../race/RaceView";

import TutorialControls from "../tutorials/TutorialControls";
import TutorialLadder from "../tutorials/TutorialLadder";
import TutorialLaps from "../tutorials/TutorialLaps";
import TutorialPowerups from "../tutorials/TutorialPowerups";

import isMobile from "ismobilejs";

export default class GameScene extends p3.Scene {
    /**
     * @param {boolean} [tutorial=false]
     * @param {boolean} [replay=false]
     */
    constructor(tutorial, replay) {
        super();

        /**
         * @member {RaceEngine}
         * @private
         */
        this._engine = null;

        /**
         * @member {Hud}
         * @private
         */
        this._hud = null;

        /**
         * @member {number}
         * @private
         */
        this._time = 0;

        /**
         * @type {number}
         * @private
         */
        this._totalLaps = 2;

        /**
         * @member {boolean}
         * @private
         */
        this._racing = false;

        /**
         * @member {Player}
         * @private
         */
        this._player = null;

        /**
         * @member {null}
         * @private
         */
        this._skybox = null;

        /**
         * @member {boolean}
         * @private
         */
        this._offroad = false;

        /**
         * @member {PIXI.RenderTexture}
         * @private
         */
        this._pauseImage = null;

        /**
         * @member {PIXI.Sprite}
         * @private
         */
        this._overlay = null;

        /**
         * @member {boolean}
         * @private
         */
        this._gameover = false;

        /**
         * @member {Array}
         * @private
         */
        this._cars = [];

        /**
         * @member {Array.<AI>}
         * @private
         */
        this._ai = [];

        /**
         * @member {Array.<Pickup>}
         * @private
         */
        this._pickups = [];

        /**
         * @member {Array.<MissilePowerup>}
         * @private
         */
        this._missilePowerups = [];

        /**
         * @member {Array.<AOEPowerup>}
         * @private
         */
        this._aoePowerups = [];

        /**
         * @member {Array.<BombPowerup>}
         * @private
         */
        this._bombPowerups = [];

        /**
         * @type {Array.<BoostPowerup>}
         * @private
         */
        this._boostPowerups = [];

        /**
         * @member {Audio}
         * @private
         */
        this._ambientSFX = null;

        /**
         * @member {TweenMax}
         * @private
         */
        this._balanceTime = window.performance.now();

        /**
         * @member {number}
         * @private
         */
        this._balanceDelayMin = 4;

        /**
         * @member {number}
         * @private
         */
        this._balanceDelayMax = 8;

        /**
         * @member {number}
         * @private
         */
        this._balanceDelay = p3.Utils.randomRange(this._balanceDelayMax, this._balanceDelayMin);

        /**
         * @type {Tutorial}
         * @private
         */
        this._tutorial = null;

        /**
         * @type {Array.<number>}
         * @private
         */
        this._tutorialRatios = [0, 0.06, 0.12, 0.172];

        /**
         * @type {number}
         * @private
         */
        this._tutorialIndex = 0;

        /**
         * @type {boolean}
         * @private
         */
        this._tutorialPause = false;

        /**
         * @type {boolean}
         * @private
         */
        this._isTutorial = tutorial;

        /**
         * @type {boolean}
         * @private
         */
        this._isReplay = replay;

        /**
         * @type {boolean}
         * @private
         */
        this._isRacing = false;

        /**
         * @type {Audio}
         * @private
         */
        this._startingRevSFX = null;
    }

    init() {
        super.init();

        Common.game = this;

        const assets = Common.assets;

        const tracks = ["track01", "track02", "track03", "track04", "track05"];
        const name = this._isTutorial ? "track01" : (this._isReplay && Common.previousTrackName) ?
            Common.previousTrackName : tracks[Math.floor(Math.random() * tracks.length)];
        console.log("track -", name);

        const data = assets.getJSON(name);
        const track = new RaceTrack(data);

        const view = new RaceView(1280);
        view.x = Common.STAGE_WIDTH * 0.5;
        view.drawDistance = isMobile ? 16 : 8;
        this.addChild(view);

        RaceEngine.getTexture = function(url) {
            return assets.getTexture(url);
        };

        this._engine = new RaceEngine(track, view);
        this._engine.init();
        Common.engine = this._engine;

        this._engine.camera.vanishingPoint = new PIXI.Point(0, Common.STAGE_HEIGHT - 350);

        this._hud = new Hud();
        this.addChild(this._hud);

        this._hud.pauseButton.signals.click.add(this.onPauseButtonClick, this);

        this._player = new Player(Common.playerDriverId, GameScene.getRaceCarView(Common.playerDriverId));
        this._player.view.y = Common.STAGE_HEIGHT * 0.5 + 246;
        this._player.offset.y = -54;
        this._player.zDepthOffset = 1;
        this._player.signals.lap.add(this.onRaceCarLap, this);
        this._engine.addEntity(this._player, 0);

        this._engine.camera.shakeOffset.x = Common.STAGE_WIDTH * 0.5;
        this._engine.camera.target = this._player;

        this._skybox = new Skybox();
        this.addChildAt(this._skybox, 0);

        this.createAI();
        this.placeObjects();

        this.start();
    }

    destroy() {
        TweenMax.killAll();

        if (this._pauseImage) {
            this._pauseImage.parent.removeChild(this._pauseImage);
            this._pauseImage.destroy();
            this._pauseImage = null;
        }
        this._player.destroy();
        for (let i = 0; i < this._ai.length; ++ i) {
            const ai = this._ai[i];
            ai.destroy();
        }
        this._ai.length = 0;

        for (let i = 0; i < this._pickups.length; ++ i) {
            const pickup = this._pickups[i];
            pickup.destroy();
        }
        this._pickups.length = 0;

        const arr = this._missilePowerups.concat(this._aoePowerups, this._bombPowerups, this._boostPowerups);
        for (let i = 0; i < arr.length; ++ i) {
            const powerup = arr[i];
            powerup.destroy();
        }
        this._missilePowerups.length = this._bombPowerups.length = this._aoePowerups.length = this._boostPowerups.length = 0;

        this.stopAmbientSFX();
        this.stopStartingRevSFX();

        super.destroy();
    }

    appear() {
        super.appear();

        if (Common.isFirstTime) {
            Common.isFirstTime = false;

            /*
            delay(function() {
                this.signals.pause.dispatch(this);
            }, 0.5, this);
            */
        }

        Common.audio.playSound("sfx_cockerel_cry_00");
        Common.audio.playMusic(["music_raceloop_00", "music_raceloop_01", "music_raceloop_02"]);
    }

    show() {
        this.resumeGame();
    }

    hide() {
    }

    /**
     * @param {function} [callback]
     * @param {*} [scope]
     */
    animateIn(callback, scope) {
    }

    /**
     * @param {function} [callback]
     * @param {*} [scope]
     */
    animateOut(callback, scope) {
    }

    start() {
        this._hud.runCountdown(0.8, go, this);

        function go() {
            this._player.moving = true;
            this._isRacing = true;

            for (let i = 0; i < this._ai.length; ++ i) {
                const ai = this._ai[i];
                ai.moving = true;
            }
            this._time = 0;

            this._player.startEngineSFX();
            this.stopStartingRevSFX();

            Common.audio.playSound("sfx_car_start_tyres_04");
        }

        this._cars = this._ai.concat([this._player]);
        console.log(this._cars);

        this._hud.ladder.signals.change.add(this.onLadderChange, this);
        this._hud.ladder.init();

        this._hud.setLaps(this._player.lap + 1, 3);
        this.playAmbientSFX();
        this.playStartingRevSFX();
    }

    end() {
        if (this._gameover) return;
        this._gameover = true;

        console.log("gameover");

        const place = this.getRacePos(this._player);
        this.signals.next.dispatch(this, place);
    }

    resize() {
        this.x = (p3.View.width - Common.STAGE_WIDTH) * 0.5;
        this._hud.resize();
        this._skybox.resize();

        if (this._tutorial) {
            this._tutorial.resize();
        }
    }

    update() {
        if (Common.paused) return;

        this._updateTutorial();

        if (this._tutorialPause) return;

        this._engine.update(deltaTime);
        this._hud.update(deltaTime);

        this._updatePowerups();
        this._updateCarsOrder();

        this._time += deltaTime * 1000;

        this.checkCollisions();
        this.checkCollisionsPickups();
        this.checkCollisionsMissiles();
        this.checkCollisionBombs();
        this.checkCollisionAOEs();

        this._skybox.update();

        this._balanceRace();
    }

    resumeGame() {
        this._hud.pauseButton.interactive = true;
        TweenMax.resumeAll(true, true, true);

        if (this._pauseImage) {
            this.visible = true;
            TweenMax.to(this._pauseImage, 0.14, {
                alpha: 0,
                ease: Power1.easeInOut,
                onComplete: function() {
                    this._pauseImage.parent.removeChild(this._pauseImage);
                    this._pauseImage = null;
                },
                onCompleteScope: this
            });
        }

        this.resumePowerups();

        this.playAmbientSFX();
        !this._isRacing && this.playStartingRevSFX();
        this._player.startEngineSFX();
        this._player.offroad && this._player.startOffroadSFX();
        !this._isRacing && this._hud.startingLights.resume();
    }

    /**
     * @param {boolean} [animate=true]
     */
    pauseGame(animate = true) {
        this._hud.pauseButton.interactive = false;
        this._hud.pauseButton.onMouseOut();
        TweenMax.pauseAll(true, true, true);

        if (animate) {
            const filter = new PIXI.filters.BlurFilter();
            filter.blur = 6;
            filter.quality = 3;

            this.filters = [filter];
            this.filterArea = new PIXI.Rectangle(0, 0, p3.View.width, p3.View.height);

            const texture = new PIXI.RenderTexture.create(p3.View.width, p3.View.height, PIXI.SCALE_MODES.DEFAULT, 1);
            Common.renderer.render(this, texture);

            this.filters = null;
            this.filterArea = null;

            this._pauseImage && this._pauseImage.parent.removeChild(this._pauseImage);

            this._pauseImage = new PIXI.Sprite(texture);
            this.parent.addChild(this._pauseImage);

            this._pauseImage.alpha = 0;
            TweenMax.to(this._pauseImage, 0.2, {
                alpha: 1,
                ease: Power1.easeInOut,
                onComplete: function() {
                    this.visible = false;
                },
                onCompleteScope: this
            });
        }

        this.pausePowerups();

        this.stopAmbientSFX();
        this.stopStartingRevSFX();
        this._player.stopEngineSFX();
        this._player.stopOffroadSFX();
        !this._isRacing && this._hud.startingLights.pause();
    }

    createAI() {
        const types = [0, 1, 2, 4, 3];
        const playerIndex = types.indexOf(Common.playerDriverId);
        if (playerIndex !== -1) {
            types.splice(playerIndex, 1);
        }

        for (let i = 0; i < types.length; ++ i) {
            const id = types[i];
            const ai = new AI(id, GameScene.getRaceCarView(id));
            ai.offset.y = -54;
            ai.zDepthOffset = 1;
            ai.signals.lap.add(this.onRaceCarLap, this);
            this._ai.push(ai);
        }

        const startingSpacing = 560;
        const arr = this._ai.slice(0);
        if (this._isTutorial) {
            arr.splice(2, 0, this._player);
        } else {
            arr.push(this._player);
        }

        for (let i = 0; i < arr.length; ++ i) {
            const car = arr[i];
            car.offset.x = ((i % 2) ? -1 : 1) * startingSpacing;

            let distance = this._engine.track.length - 2;
            distance -= i * 1.4;
            this._engine.addEntity(car, distance);
        }
    }

    placeObjects() {
        const assets = Common.assets;

        for (let i = 0; i < this._engine.track.objects.length; ++ i) {
            const data = this._engine.track.objects[i];
            switch (data.id) {
                case 0 : {
                    const object = new RaceEntity(RaceEntity.TYPE_STATIC, new PIXI.Sprite(assets.getTexture(data.t)));
                    object.offset.x = data.o.x * Common.RACE_ENTITY_SCALE;
                    object.offset.y = 240.0;
                    object.scale.x = 6;
                    object.scale.y = 6;
                    object.view.anchor = new PIXI.Point(0.5, 1.0);
                    this._engine.addEntity(object, data.d, false);
                    break;
                }
                case 1 : {
                    const object = new Pickup();
                    object.offset.x = data.o.x * Common.RACE_ENTITY_SCALE;
                    object.offset.y = -120.0;
                    object.zDepthOffset = 1;
                    object.signals.collect.add(this.onPickupCollect, this);
                    object.init();
                    this._engine.addEntity(object, data.d);
                    this._pickups.push(object);
                    break;
                }
            }
        }
    }

    checkCollisions() {
        const cars = this.getCars();
        for (let i = 0; i < cars.length; ++ i) {
            const cara = cars[i];
            let hit = false;

            for (let j = 0; j < cars.length; ++ j) {
                const carb = cars[j];
                if (cara === carb) continue;

                const diff = cara.getDistanceTo(carb);
                const a = cara.distance;
                const b = a + diff;

                const collision = this._engine.testCollision(
                    new PIXI.Point(cara.offset.x, a),
                    new PIXI.Point(carb.offset.x, b),
                    new PIXI.Point(cara.collisionWidth, cara.collisionLength),
                    new PIXI.Point(carb.collisionWidth, carb.collisionLength)
                );

                if (collision) {
                    cara.onCollisionBegin(collision, carb);
                    carb.onCollisionBegin(collision, cara);
                    hit = true;

                    if (collision.top) {
                        cara.distance = carb.distance +
                            ((cara.collisionLength + carb.collisionLength) / this._engine.view.segmentHeight);
                    }
                    if (collision.bottom) {
                        cara.distance = carb.distance -
                            ((cara.collisionLength + carb.collisionLength) / this._engine.view.segmentHeight);
                    }
                    if (collision.left) {
                        cara.offset.x = carb.offset.x - (cara.collisionWidth + carb.collisionWidth) * 0.5;
                    }
                    if (collision.right) {
                        cara.offset.x = carb.offset.x + (cara.collisionWidth + carb.collisionWidth) * 0.5;
                    }
                    cara.distance = (cara.distance + this._engine.track.length) % this._engine.track.length;

                    if (collision.bottom) {
                        if (cara.velocity < carb.velocity) {
                            carb.velocity = cara.velocity;
                        } else if (carb.velocity < cara.velocity) {
                            cara.velocity = carb.velocity;
                        }
                    }
                    if (collision.left || collision.right) {
                        if ((Math.abs(cara.turnVelocity) + Math.abs(carb.turnVelocity)) > 1200) {
                            cara.turnVelocity *= -0.9;
                            carb.turnVelocity *= -0.9;
                        } else {
                            cara.turnVelocity = 0;
                            carb.turnVelocity = 0;
                        }
                    }
                }
            }
            !hit && cara.onCollisionEnd();
        }
    }

    checkCollisionsPickups() {
        const cars = this.getCars();
        for (let i = 0; i < cars.length; ++ i) {
            const car = cars[i];

            for (let i = 0; i < this._pickups.length; ++ i) {
                const pickup = this._pickups[i];
                const collision = this._engine.testCollision(
                    new PIXI.Point(car.offset.x, car.distance),
                    new PIXI.Point(pickup.offset.x, pickup.distance),
                    new PIXI.Point(car.collisionWidth, car.collisionLength),
                    new PIXI.Point(pickup.collisionWidth, car.collisionLength)
                );
                if (collision && !car.usingPowerup) {
                    if (pickup.collect(car)) {
                        car.usingPowerup = true;
                    }
                }
            }
        }
    }

    checkCollisionsMissiles() {
        const cars = this.getCars();
        for (let i = 0; i < cars.length; ++ i) {
            const car = cars[i];

            for (let j = 0; j < this._missilePowerups.length; ++ j) {
                const powerup = this._missilePowerups[j];
                if (!powerup.missile.armed) continue;

                const diff = car.getDistanceTo(powerup.missile);
                const a = car.distance;
                const b = a + diff;

                if (car !== powerup.missile.owner) {
                    const collision = this._engine.testCollision(
                        new PIXI.Point(car.offset.x, a),
                        new PIXI.Point(powerup.missile.offset.x, b),
                        new PIXI.Point(car.collisionWidth, car.collisionLength),
                        new PIXI.Point(powerup.missile.collisionWidth, powerup.missile.collisionLength)
                    );
                    if (collision) {
                        powerup.missile.explode(car);
                        car.takeHit(powerup.missile.owner.isPlayer || car.isPlayer);

                        (powerup.missile.owner === this._player || car === this._player) && this._hud.ladder.usePowerup(powerup.missile.powerupType, car, true);
                        (car === this._player) && this._engine.camera.applyShake(1, 30);
                    }
                }
            }
        }
    }

    checkCollisionBombs() {
        const cars = this.getCars();
        for (let i = 0; i < cars.length; ++ i) {
            const car = cars[i];

            for (let i = 0; i < this._bombPowerups.length; ++ i) {
                const powerup = this._bombPowerups[i];
                const collision = this._engine.testCollision(
                    new PIXI.Point(car.offset.x, car.distance),
                    new PIXI.Point(powerup.bomb.offset.x, powerup.bomb.distance),
                    new PIXI.Point(car.collisionWidth, car.collisionLength),
                    new PIXI.Point(powerup.bomb.collisionWidth, car.collisionLength)
                );
                if (collision && powerup.bomb.armed) {
                    powerup.bomb.explode(car);
                    car.takeHit(powerup.bomb.owner.isPlayer || car.isPlayer);

                    (powerup.bomb.owner === this._player || car === this._player) && this._hud.ladder.usePowerup(powerup.bomb.powerupType, car, true);
                    (car === this._player) && this._engine.camera.applyShake(1, 30);
                }
            }
        }
    }

    checkCollisionAOEs() {
        const cars = Common.game.getCars();

        for (let i = 0; i < this._aoePowerups.length; ++ i) {
            const powerup = this._aoePowerups[i];
            for (let j = 0; j < cars.length; ++ j) {
                const car = cars[j];
                if (powerup.aoe.owner === car) continue;

                const diff = powerup.aoe.owner.getDistanceTo(car);
                if (!car.debuffed && (Math.abs(diff) <= powerup.aoe.range)) {
                    car.applyDebuff(3, powerup.aoe.slow, powerup.aoe.swerve);
                    (powerup.aoe.owner === this._player || car === this._player) && this._hud.ladder.usePowerup(powerup.aoe.powerupType, car, true);
                    (car === this._player) && this._engine.camera.applyShake(3, 14);
                }
            }
        }
    }

    playAmbientSFX() {
        const params = new AudioParams();
        params.loop = true;
        this._ambientSFX = Common.audio.playSound([
            "sfx_generic_race_loop_00",
            "sfx_generic_race_loop_01",
            "sfx_generic_race_loop_02",
            "sfx_ambient_wacky_loop_03"
        ], params);
    }

    stopAmbientSFX() {
        if (this._ambientSFX) {
            this._ambientSFX.stop();
            this._ambientSFX = null;
        }
    }

    playStartingRevSFX() {
        const params = new AudioParams();
        params.loop = true;
        this._startingRevSFX = Common.audio.playSound("sfx_race_start_idle_revs_00", params);
    }

    stopStartingRevSFX() {
        if (this._startingRevSFX) {
            this._startingRevSFX.stop();
            this._startingRevSFX = null;
        }
    }

    resumePowerups() {
        const arr = this._missilePowerups.concat(this._aoePowerups, this._bombPowerups, this._boostPowerups);
        for (let i = 0; i < arr.length; ++ i) {
            const powerup = arr[i];
            powerup.resume();
        }
    }

    pausePowerups() {
        const arr = this._missilePowerups.concat(this._aoePowerups, this._bombPowerups, this._boostPowerups);
        for (let i = 0; i < arr.length; ++ i) {
            const powerup = arr[i];
            powerup.pause();
        }
    }

    /**
     * @private
     */
    _updatePowerups() {
        for (let i = this._missilePowerups.length - 1; i >= 0; -- i) {
            const powerup = this._missilePowerups[i];
            if (powerup.destroyNextFrame) {
                this._missilePowerups.splice(i, 1);
            } else {
                powerup.update(deltaTime);
            }
        }
        for (let i = this._aoePowerups.length - 1; i >= 0; -- i) {
            const powerup = this._aoePowerups[i];
            if (powerup.destroyNextFrame) {
                this._aoePowerups.splice(i, 1);
            } else {
                powerup.update(deltaTime);
            }
        }
        for (let i = this._bombPowerups.length - 1; i >= 0; -- i) {
            const powerup = this._bombPowerups[i];
            if (powerup.destroyNextFrame) {
                this._bombPowerups.splice(i, 1);
            } else {
                powerup.update(deltaTime);
            }
        }
        for (let i = this._boostPowerups.length - 1; i >= 0; -- i) {
            const powerup = this._boostPowerups[i];
            if (powerup.destroyNextFrame) {
                this._boostPowerups.splice(i, 1);
            } else {
                powerup.update(deltaTime);
            }
        }
    }

    /**
     * @private
     */
    _updateCarsOrder() {
        this._cars.sort(function(a, b) {
            return b.getTotalDistance() - a.getTotalDistance();
        });
    }

    /**
     * @private
     */
    _updateTutorial() {
        if (!this._isTutorial) return;

        const input = Common.input;
        input.isKeyPressed(32) && this._dismissTutorial();

        if (this._player.lap !== 1 || (this._tutorialIndex >= this._tutorialRatios.length)) return;

        const ratio = this._tutorialRatios[this._tutorialIndex];
        if (Math.floor(this._player.distance) / this._engine.track.length >= ratio) {
            this._tutorialPause = true;
            this.pauseGame(false);

            let tutorial;
            switch (this._tutorialIndex ++) {
                case 0 : {
                    tutorial = new TutorialControls();
                    tutorial.show();
                    this.addChild(tutorial);
                    break;
                }
                case 1 : {
                    tutorial = new TutorialLadder();
                    tutorial.show();
                    this.addChild(tutorial);
                    break;
                }
                case 2 : {
                    tutorial = new TutorialLaps();
                    tutorial.show();
                    this.addChild(tutorial);
                    break;
                }
                case 3 : {
                    tutorial = new TutorialPowerups();
                    tutorial.show();
                    this.addChild(tutorial);
                    break;
                }
            }
            tutorial.resize();

            delay(this._dismissTutorial, 4, this);

            this._tutorial = tutorial;
        }
    }

    _dismissTutorial() {
        if (!this._tutorial) return;

        this._tutorial.hide();
        this._tutorial = null;
        this._tutorialPause = false;
        this.resumeGame();
    }

    /**
     * @private
     */
    _balanceRace() {
        if (this.getCars().length <= 1) return;

        const time = (window.performance.now() - this._balanceTime) / 1000;
        if (time > this._balanceDelay) {
            this._balanceTime = window.performance.now();

            let distance = 0;
            let count = 0;
            const cars = this.getCars();
            for (let i = 0; i < cars.length; ++ i) {
                const cara = cars[i];
                for (let j = 0; j < cars.length; ++ j) {
                    const carb = cars[j];
                    if (cara !== carb) {
                        distance += Math.abs(carb.getTotalDistance() - cara.getTotalDistance());
                        ++ count;
                    }
                }
            }
            const scalar = Math.max(0, Math.min(1.4, (distance / count) / 12));
            console.log("scalar:", scalar);

            distance = 0;
            count = 0;
            for (let i = 0; i < cars.length; ++ i) {
                for (let j = 0; j < cars.length; ++ j) {
                    const carb = cars[j];
                    if (this._player !== carb) {
                        distance += carb.getTotalDistance() - this._player.getTotalDistance();
                        ++ count;
                    }
                }
            }
            const avgPlayerDist = (distance / count);
            console.log("avgPlayerDist:", avgPlayerDist);

            for (let i = 0; i < cars.length; ++ i) {
                const car = cars[i];
                const racePos = this.getRacePos(car);

                let modifier;
                if (racePos === 0) {
                    if (car.isPlayer) {
                        modifier = -0.01;
                    } else {
                        modifier = -0.026;
                    }
                } else if (racePos === 1) {
                    if (car.isPlayer) {
                        modifier = 0.012;
                    } else {
                        modifier = 0;
                    }
                } else if (racePos === 2) {
                    if (car.isPlayer) {
                        modifier = 0.026;
                    } else {
                        modifier = 0.02;
                    }
                } else if (racePos === 3) {
                    if (car.isPlayer) {
                        modifier = 0.04;
                    } else {
                        modifier = 0.03;
                    }
                } else if (racePos === 4) {
                    if (car.isPlayer) {
                        modifier = 0.054;
                    } else {
                        modifier = 0.04;
                    }
                }

                // if player is too far away slow all AI down
                if (avgPlayerDist > 4 && !car.isPlayer) {
                    modifier = -0.06;
                }

                car.speedModifer = 1 + modifier * scalar;
                console.log("place:", i, "modifier:", car.speedModifer);
            }
            this._balanceDelay = p3.Utils.randomRange(this._balanceDelayMax, this._balanceDelayMin);
        }
    }

    /**
     * @param {Pickup} pickup
     * @param {RaceCar} collector
     */
    onPickupCollect(pickup, collector) {
        const isPlayer = (collector.id === Common.playerDriverId);

        const selector = new PickupSelector(isPlayer);
        selector.x = pickup.view.x;
        selector.y = pickup.view.y;
        selector.visible = isPlayer;
        selector.signals.select.add(function(selector, type) {
            let powerup;
            switch (type) {
                case 0 : {
                    powerup = new BombPowerup(collector);
                    powerup.type = type;
                    powerup.activate();
                    this._bombPowerups.push(powerup);
                    break;
                }
                case 1 : {
                    powerup = new CandyPowerup(collector);
                    powerup.type = type;
                    powerup.activate();
                    this._bombPowerups.push(powerup);
                    break;
                }
                case 2 : {
                    powerup = new FlamePowerup(collector);
                    powerup.type = type;
                    powerup.activate();
                    this._boostPowerups.push(powerup);
                    break;
                }
                case 3 : {
                    powerup = new GluePowerup(collector);
                    powerup.type = type;
                    powerup.activate();
                    this._bombPowerups.push(powerup);
                    break;
                }
                case 4 : {
                    powerup = new LightingPowerup(collector);
                    powerup.type = type;
                    powerup.activate();
                    this._boostPowerups.push(powerup);
                    break;
                }
                case 5 : {
                    powerup = new MissilePowerup(collector);
                    powerup.type = type;
                    powerup.activate();
                    this._missilePowerups.push(powerup);
                    break;
                }
                case 6 : {
                    powerup = new TacsPowerup(collector);
                    powerup.type = type;
                    powerup.activate();
                    this._bombPowerups.push(powerup);
                    break;
                }
                case 7 : {
                    powerup = new SmartMissilePowerup(collector);
                    powerup.type = type;
                    powerup.activate();
                    this._missilePowerups.push(powerup);
                    break;
                }
                case 8 : {
                    powerup = new SteamPowerup(collector);
                    powerup.type = type;
                    powerup.activate();
                    this._boostPowerups.push(powerup);
                    break;
                }
                case 9 : {
                    powerup = new BubblesPowerup(collector);
                    powerup.type = type;
                    powerup.activate();
                    this._aoePowerups.push(powerup);
                    break;
                }
                case 10 : {
                    powerup = new ButterfliesPowerup(collector);
                    powerup.type = type;
                    powerup.activate();
                    this._aoePowerups.push(powerup);
                    break;
                }
            }
            (collector === this._player) && this._hud.ladder.usePowerup(type, collector);
        }, this);
        this._engine.view.addChild(selector);

        const to = selector.y - 140;
        TweenMax.to(selector, 0.4, {
            y: to,
            ease: Power1.easeOut,
            onComplete: function() {
                selector.start();
            },
            onCompleteScope: this
        });

        selector.scale = new PIXI.Point(0.2, 0.2);
        TweenMax.to(selector.scale, 0.4, {
            x: 1,
            y: 1,
            ease: Back.easeOut,
            easeParams: [2]
        });
    }

    /**
     * @param {RaceCar} car
     * @param {number} lap
     */
    onRaceCarLap(car, lap) {
        console.log(car, lap);

        if ((lap - 1) >= this._totalLaps && car === this._player) {
            this.end();
        } else if (car === this._player) {
            this._hud.setLaps(Math.max(1, Math.min(this._totalLaps, this._player.lap)), this._totalLaps);
        }
    }

    /**
     * @param {RaceAvatar} avatar
     * @param {number} pos
     */
    onLadderChange(avatar, pos) {
        if (avatar.isPlayer) {
            console.log(avatar, pos);
            Common.audio.playSound("sfx_change_race_position_00");
        }
    }

    /**
     * @param {p3.Button} button
     */
    onPauseButtonClick(button) {
        this.signals.pause.dispatch(this);
    }

    /**
     * @param {number} id
     * @returns {RaceCarView}
     * @static
     */
    static getRaceCarView(id) {
        let view;
        switch (id) {
            case 0 : {
                view = new PussycatView();
                break;
            }
            case 1 : {
                view = new TurboView();
                break;
            }
            case 2 : {
                view = new MeanMachineView();
                break;
            }
            case 3 : {
                view = new DoomBuggyView();
                break;
            }
            case 4 : {
                view = new IQView();
                break;
            }
        }
        return view;
    }

    /**
     * @returns {Array.<AI>}
     */
    getCars() {
        return this._cars.slice(0);
    }

    /**
     * @returns {Array.<Powerup>}
     */
    getPickups() {
        return this._pickups.slice(0);
    }

    /**
     * @param car
     * @returns {number}
     */
    getRacePos(car) {
        return this._cars.indexOf(car);
    }
}
